import React, { useEffect, useRef } from 'react';
import { Search, X, Command as CommandIcon } from 'lucide-react';
import useCommandPalette from './useCommandPalette';
import CommandList from './CommandList';

/**
 * Globale Command-Palette (Cmd+K / Ctrl+K).
 *
 * Render-Regel: haengt sich an AppLayout (einmal pro App-Instanz).
 * Öffnen/Schliessen wird ueber useGlobalShortcuts() + Header-Button
 * gesteuert — diese Komponente reagiert ausschliesslich auf den Store.
 */
export default function CommandPalette() {
  const {
    open,
    query,
    setQuery,
    closePalette,
    highlightIndex,
    setHighlightIndex,
    moveHighlight,
    flatResults,
    groupedResults,
    sectionsOrder,
    executeCommand,
  } = useCommandPalette();

  const inputRef = useRef(null);

  // Autofocus beim Oeffnen, Blur beim Schliessen
  useEffect(() => {
    if (open) {
      // naechsten Frame abwarten, damit das DOM existiert
      const t = requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
      return () => cancelAnimationFrame(t);
    }
    inputRef.current?.blur();
  }, [open]);

  // Scroll-Lock, solange Palette offen ist
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const total = flatResults.length;

  function onKeyDown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      closePalette();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveHighlight(+1, total);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveHighlight(-1, total);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const cmd = flatResults[highlightIndex];
      if (cmd) executeCommand(cmd);
      return;
    }
    if (e.key === 'Home') {
      e.preventDefault();
      setHighlightIndex(0);
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      if (total > 0) setHighlightIndex(total - 1);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center pt-[12vh] px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Command-Palette"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 dark:bg-black/70 backdrop-blur-sm"
        onClick={closePalette}
      />

      {/* Panel */}
      <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl dark:shadow-slate-950/80 border border-gray-200 dark:border-slate-700/80 overflow-hidden flex flex-col max-h-[80vh]">
        {/* Search-Bar */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-slate-700/80">
          <Search size={18} className="text-gray-400 dark:text-slate-500 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Befehl suchen oder Seite oeffnen ..."
            autoComplete="off"
            spellCheck="false"
            className="flex-1 bg-transparent border-0 outline-none text-[15px] text-gray-900 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500"
          />
          <button
            onClick={closePalette}
            className="text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 transition-colors"
            title="Schliessen (Esc)"
            aria-label="Schliessen"
          >
            <X size={18} />
          </button>
        </div>

        {/* Ergebnisse */}
        <div className="flex-1 overflow-y-auto">
          {total > 0 ? (
            <CommandList
              groupedResults={groupedResults}
              sectionsOrder={sectionsOrder}
              flatResults={flatResults}
              highlightIndex={highlightIndex}
              onSelect={executeCommand}
              onHover={(i) => setHighlightIndex(i)}
            />
          ) : (
            <EmptyState query={query} />
          )}
        </div>

        {/* Footer / Hint-Bar */}
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-gray-200 dark:border-slate-700/80 bg-gray-50 dark:bg-slate-900/80">
          <div className="flex items-center gap-3 text-[11px] text-gray-500 dark:text-slate-500">
            <HintKey label="Navigieren" keys={['↑', '↓']} />
            <HintKey label="Waehlen" keys={['Enter']} />
            <HintKey label="Schliessen" keys={['Esc']} />
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-gray-400 dark:text-slate-500">
            <CommandIcon size={11} /> K
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Empty-State ────────────────────────────────────────────────────
function EmptyState({ query }) {
  return (
    <div className="py-12 px-6 flex flex-col items-center text-center">
      <div className="w-12 h-12 rounded-2xl bg-gray-100 dark:bg-slate-800 flex items-center justify-center mb-3">
        <Search size={20} className="text-gray-400 dark:text-slate-500" />
      </div>
      <div className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
        {query ? 'Kein Treffer' : 'Was moechtest du tun?'}
      </div>
      <div className="text-xs text-gray-400 dark:text-slate-500 max-w-xs">
        Druecke <kbd className="px-1 py-0.5 rounded bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-[10px] font-mono">⌘K</kbd> von
        {' '}ueberall, oder <kbd className="px-1 py-0.5 rounded bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-[10px] font-mono">g</kbd> →
        {' '}<kbd className="px-1 py-0.5 rounded bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-[10px] font-mono">k</kbd> fuer Keywords.
      </div>
    </div>
  );
}

// ── kleiner Hint-Key (Footer) ──────────────────────────────────────
function HintKey({ label, keys }) {
  return (
    <span className="flex items-center gap-1">
      {keys.map((k, i) => (
        <kbd
          key={i}
          className="px-1.5 py-0.5 rounded bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-[10px] font-mono"
        >
          {k}
        </kbd>
      ))}
      <span>{label}</span>
    </span>
  );
}
